<template>
    <div class="contact-data-section form" v-if="isOrderApplication && ordererRequirements != null">
        <div v-if="showEnterContactDataSection">
            <ContactDataButton
                :onClickOpenProfileView="props.onClickOpenProfileView"
                :isInOperatorApp="props.isInOperatorApp"
            />
        </div>
        <div style="margin: 0 26px" v-if="showEnterSingleContactDataSection">
            <EnterSingleContactDataSection
                :onEndEditing="onEndEditing"
                :onStartEditing="onStartEditing"
                :is-in-operator-app="props.isInOperatorApp"
                ref="enterSingleContactDataSectionComponent"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IContactDataValidationResult } from "@/components/pay/ContactDataValidationResult";
    import type { IEnteredContactData } from "@/components/pay/EnteredContactData";
    import { OrdererRegisteredRequirement } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { useIdentityStore } from "@/data/stores/identity";
    import { logInformation } from "@/shared/logging/informations";
    import { computed, ref } from "vue";

    import ContactDataButton from "@/components/pay/ContactDataButton.vue";
    import EnterSingleContactDataSection from "@/components/pay/EnterSingleContactDataSection.vue";

    const cart = useCartStore();
    const identity = useIdentityStore();

    // props
    const props = defineProps<{
        isInOperatorApp: boolean;
        onClickOpenProfileView?: () => void;
        onStartEditing?: () => void;
        onEndEditing?: () => void;
    }>();

    // computed

    const showEnterSingleContactDataSection = computed<boolean>(() => {
        const value =
            ordererRequirements.value?.enabledFieldRequirementsCount === 1 ||
            ((identity.isWaiterInCurrentLocation || identity.isAdmin) &&
                ordererRequirements.value?.enabledFieldRequirementsCount === 0 &&
                isRegisteredRequired.value);
        logInformation("showEnterSingleContactDataSection: " + value);
        return value;
    });

    const showEnterContactDataSection = computed<boolean>(() => {
        const value =
            (ordererRequirements.value?.enabledFieldRequirementsCount ?? 0) > 1 ||
            ((identity.isWaiterInCurrentLocation || identity.isAdmin) &&
                ordererRequirements.value?.enabledFieldRequirementsCount === 1 &&
                isRegisteredRequired.value);

        return value;
    });

    const isOrderApplication = computed<boolean>(() => {
        return cart.orderService?.useCase != null;
    });

    const ordererRequirements = computed(() => {
        return cart.orderService?.useCase?.ordererRequirements;
    });

    const enterSingleContactDataSectionComponent = ref<
        InstanceType<typeof EnterSingleContactDataSection> | undefined
    >();

    const isRegisteredRequired = computed<boolean>(
        () => ordererRequirements.value?.registeredRequirement == OrdererRegisteredRequirement.Required
    );

    function getCurrentContactData(): IEnteredContactData {
        if (enterSingleContactDataSectionComponent.value != null) {
            return enterSingleContactDataSectionComponent.value!.getCurrentContactData();
        }

        const currentContactData =
            cart.currentCart?.contactDataEndUser == null ? undefined : cart.currentCart?.contactDataEndUser;
        return {
            firstName: currentContactData?.firstName,
            lastName: currentContactData?.lastName,
            email: currentContactData?.email,
            phoneNumberCountryCode: undefined,
            phoneNumber: currentContactData?.phoneNumber,
        };
    }

    // showMissingInfoPopup - currently it works only for single contact data section and when an identity is a waiter or admin
    async function validateAndCreateOrUpdateContactData(
        showMissingInfoPopup: boolean = false
    ): Promise<IContactDataValidationResult> {
        if (enterSingleContactDataSectionComponent.value != null) {
            return await enterSingleContactDataSectionComponent.value!.validateAndCreateOrUpdateContactData(
                showMissingInfoPopup
            );
        } else {
            return {
                contactData:
                    cart.currentCart?.contactDataEndUser == null ? undefined : cart.currentCart?.contactDataEndUser,
                isValid: cart.currentCart?.contactDataEndUser != null,
            };
        }
    }

    function reset() {
        if (enterSingleContactDataSectionComponent.value != null) {
            enterSingleContactDataSectionComponent.value.reset();
        }
    }

    defineExpose({
        getCurrentContactData,
        validateAndCreateOrUpdateContactData,
        reset,
    });
</script>

<style scoped lang="scss"></style>
